import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { NavLink, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { logout } from '@alphapoint/apex-web/lib/redux/actions/authActions';
import APIcon from '@alphapoint/apex-web/lib/components/common/APIcon';
import APLogo from '@alphapoint/apex-web/lib/components/common/APLogo/APLogo';
import { getBEMClasses } from '../../helpers/cssClassesHelper';
import './MobileNavigation.css';
import APButton from '@alphapoint/apex-web/lib/components/common/APButton';

const mobileNavClasses = getBEMClasses('mobile-nav');

const MobileNavigationComponent = ({
  isOpen,
  onClose,
  navItems,
  isAuthenticated,
  systemMarginEnabled,
  marginBorrowerEnabled,
  systemFuturesEnabled,
  futuresEnabled,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const history = useHistory();
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsVisible(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
    } else if (isVisible) {
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
        document.body.style.overflow = '';
      }, 300); // Match animation duration
      return () => clearTimeout(timer);
    }
  }, [isOpen, isVisible]);

  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  if (!isVisible) return null;

  const handleNavClick = () => {
    onClose();
  };

  const handleSignOutClick = () => {
    history.push('/login');
    dispatch(logout());
    onClose();
  };

  const handleEnableLeverageClick = () => {
    history.push('/settings/trade');
    onClose();
  };

  return (
    <div className={mobileNavClasses('overlay', isClosing ? 'closing' : '')}>
      <div className={mobileNavClasses('content', isClosing ? 'closing' : '')}>
        <div className={mobileNavClasses('header')}>
          <APLogo customClass={mobileNavClasses('logo')} linkTo="/" />
        </div>
        {isAuthenticated &&
          ((systemMarginEnabled && !marginBorrowerEnabled) || (systemFuturesEnabled && !futuresEnabled)) && (
            <APButton
              onClick={handleEnableLeverageClick}
              classModifiers={mobileNavClasses('enable-margin')}
              type="button">
              {t('Enable Leveraged Trading', { ns: 'core' })}
            </APButton>
          )}
        <ul className={mobileNavClasses('list')}>
          {navItems.map((item) => {
            const hasSubmenu = item.submenuNavItems && item.submenuNavItems.length > 0;

            return (
              <li key={item.text} className={mobileNavClasses('item')}>
                {hasSubmenu ? (
                  <div className={mobileNavClasses('parent')}>
                    <span className={mobileNavClasses('label')}>{t(item.text)}</span>
                    <ul className={mobileNavClasses('submenu')}>
                      {item.submenuNavItems.map((submenuItem) => (
                        <li key={submenuItem.path} className={mobileNavClasses('submenu-item')}>
                          <NavLink
                            to={submenuItem.path}
                            onClick={handleNavClick}
                            className={mobileNavClasses('link')}
                            activeClassName={mobileNavClasses('link--active')}>
                            <APIcon name={submenuItem.icon} />
                            <div className={mobileNavClasses('link-content')}>
                              <span className={mobileNavClasses('link-label')}>{t(submenuItem.text)}</span>
                              <span className={mobileNavClasses('link-subtitle')}>{t(submenuItem.subtitle)}</span>
                            </div>
                          </NavLink>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : item.isSignOut ? (
                  <div onClick={handleSignOutClick} className={mobileNavClasses('link')}>
                    <span className={mobileNavClasses('label')}>{t(item.text)}</span>
                  </div>
                ) : (
                  <NavLink
                    to={item.path}
                    onClick={handleNavClick}
                    className={mobileNavClasses('link')}
                    activeClassName={mobileNavClasses('link--active')}>
                    <span className={mobileNavClasses('label')}>{t(item.text)}</span>
                  </NavLink>
                )}
              </li>
            );
          })}
        </ul>
      </div>
      <div className={mobileNavClasses('backdrop', isClosing ? 'closing' : '')} onClick={onClose} />
    </div>
  );
};

MobileNavigationComponent.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  navItems: PropTypes.array.isRequired,
};

export default MobileNavigationComponent;
